import React from 'react';
import {
  Button,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  type FoundationApplicationDetail,
  type FoundationFormAnswerInput,
  type FoundationInstitutionSearchResult,
  type FoundationReviewAction,
} from '../../../endpoints/foundation';
import FoundationFormRenderer from './FoundationFormRenderer';

type Props = {
  application: FoundationApplicationDetail;
  busy: boolean;
  comment: string;
  onCommentChange: (v: string) => void;
  studentAnswers: FoundationFormAnswerInput[];
  onStudentAnswersChange: (next: FoundationFormAnswerInput[]) => void;
  institutionQuery: string;
  onInstitutionQueryChange: (v: string) => void;
  searchItems: FoundationInstitutionSearchResult[];
  canReview: boolean;
  canManage: boolean;
  installmentCount: string;
  onInstallmentCountChange: (v: string) => void;
  date1: string;
  date2: string;
  onDate1Change: (v: string) => void;
  onDate2Change: (v: string) => void;
  payeeName: string;
  onPayeeNameChange: (v: string) => void;
  achLast4: string;
  achPlaintext: string;
  achToken: string;
  onAchLast4Change: (v: string) => void;
  onAchPlaintextChange: (v: string) => void;
  onAchTokenChange: (v: string) => void;
  onUploadClick: () => void;
  onComment: () => void;
  onTrusteeApprove: () => void;
  onEoApprove: () => void;
  onStudentVerified: () => void;
  onOverrideHold: () => void;
  onDenyHold: () => void;
  onMatchInstitution: (id: string) => void;
  onChangeSchool: () => void;
  onException: () => void;
  onSaveAch: () => void;
  onFinalApprove: () => void;
};

function actionLabel(a: FoundationReviewAction): string {
  if (a.action_type === 'comment') return 'Comment';
  if (a.action_type === 'deny') return 'Denied';
  if (a.review_step_key === 'trustee_review') return 'Trustee approved';
  if (a.review_step_key === 'eo_review') return 'EO approved';
  if (a.action_type === 'approve') return 'Approved';
  return a.action_type;
}

export default function FoundationReviewPanel({
  application,
  busy,
  comment,
  onCommentChange,
  studentAnswers,
  onStudentAnswersChange,
  institutionQuery,
  onInstitutionQueryChange,
  searchItems,
  canReview,
  canManage,
  installmentCount,
  onInstallmentCountChange,
  date1,
  date2,
  onDate1Change,
  onDate2Change,
  payeeName,
  onPayeeNameChange,
  achLast4,
  achPlaintext,
  achToken,
  onAchLast4Change,
  onAchPlaintextChange,
  onAchTokenChange,
  onUploadClick,
  onComment,
  onTrusteeApprove,
  onEoApprove,
  onStudentVerified,
  onOverrideHold,
  onDenyHold,
  onMatchInstitution,
  onChangeSchool,
  onException,
  onSaveAch,
  onFinalApprove,
}: Props) {
  const amount = Number(application.awarded_amount || 0);
  const stage = application.stage;
  const gates = application.gates;
  const actions = application.review_actions || [];
  const inst = application.institution;
  const matchStatus = inst?.match_status;
  const isMatched = matchStatus === 'matched';
  const isException = matchStatus === 'exception_approved';
  const schoolOpen = !inst || matchStatus === 'provisional';
  const matchedLabel = [
    inst?.matched_name || inst?.provisional_name,
    inst?.matched_city,
    inst?.matched_state_code,
  ]
    .filter(Boolean)
    .join(', ');
  const schoolHeadline = !inst
    ? 'School: not yet named'
    : isMatched
      ? `Matched: ${matchedLabel || 'catalog school'}`
      : `School: ${inst.match_status}${
          inst.provisional_name ? ` — ${inst.provisional_name}` : ''
        }`;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Review</Typography>
        <Typography variant="body2" color="text.secondary">
          {schoolHeadline}
        </Typography>

        {actions.length > 0 && (
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Activity</Typography>
            {actions.map((a) => (
              <Stack key={a.id} spacing={0.25}>
                <Typography variant="caption" color="text.secondary">
                  {a.actor_name || 'Staff'} · {actionLabel(a)} ·{' '}
                  {a.created_at ? new Date(a.created_at).toLocaleString() : ''}
                </Typography>
                {a.body ? (
                  <Typography variant="body2">{a.body}</Typography>
                ) : null}
              </Stack>
            ))}
          </Stack>
        )}

        {canReview && (
          <>
            <TextField
              label="Comment"
              value={comment}
              onChange={(e) => onCommentChange(e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
            <Button disabled={busy || !comment.trim()} onClick={onComment}>
              Add comment
            </Button>
          </>
        )}

        {canReview && stage === 'gate_trustee_hold' && (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="contained"
              disabled={busy}
              onClick={onOverrideHold}
            >
              Override gate
            </Button>
            <Button color="error" disabled={busy} onClick={onDenyHold}>
              Deny
            </Button>
          </Stack>
        )}

        {canReview && stage === 'parallel_review' && (
          <>
            <Divider />
            <Typography variant="subtitle2">Decisions</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="contained"
                disabled={busy || gates?.trustee_review_status === 'approved'}
                onClick={onTrusteeApprove}
              >
                Trustee approve
              </Button>
              <Button
                variant="contained"
                disabled={busy || gates?.io_review_status === 'approved'}
                onClick={onEoApprove}
              >
                EO approve
              </Button>
              <Button
                disabled={
                  busy ||
                  gates?.student_verification_status === 'complete' ||
                  gates?.student_verification_status === 'waived'
                }
                onClick={onStudentVerified}
              >
                Mark student verified
              </Button>
            </Stack>
            <FoundationFormRenderer
              formKind="student_verification"
              academicYear={application.academic_year}
              value={studentAnswers}
              onChange={onStudentAnswersChange}
              applicationId={application.id}
              disabled={busy}
              title="Student verification form"
            />
            <Divider />
            <Typography variant="subtitle2">Accredited school</Typography>
            {isMatched ? (
              <>
                <Typography variant="body2">
                  Matched: {matchedLabel || 'catalog school'}
                </Typography>
                {(canReview || canManage) && (
                  <Button
                    disabled={busy}
                    onClick={onChangeSchool}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Change school
                  </Button>
                )}
              </>
            ) : (
              <>
                {schoolOpen ? (
                  <Typography variant="body2" color="text.secondary">
                    Search DAPIP and match, or approve an exception if the
                    school cannot be listed.
                  </Typography>
                ) : isException ? (
                  <Typography variant="body2">
                    Exception approved
                    {inst?.provisional_name
                      ? ` — ${[
                          inst.provisional_name,
                          inst.provisional_city,
                          inst.provisional_state,
                        ]
                          .filter(Boolean)
                          .join(', ')}`
                      : ''}
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    Match status: {inst?.match_status}
                  </Typography>
                )}
                <TextField
                  label="Search accredited institutions"
                  value={institutionQuery}
                  onChange={(e) => onInstitutionQueryChange(e.target.value)}
                  fullWidth
                />
                {searchItems.slice(0, 8).map((row) => (
                  <Button
                    key={row.id}
                    size="small"
                    disabled={busy}
                    onClick={() => onMatchInstitution(row.id)}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Match: {row.name}
                    {row.city || row.state_code
                      ? ` (${[row.city, row.state_code]
                          .filter(Boolean)
                          .join(', ')})`
                      : ''}
                  </Button>
                ))}
                {schoolOpen ? (
                  <Button disabled={busy} onClick={onException}>
                    Approve institution exception
                  </Button>
                ) : null}
              </>
            )}
          </>
        )}

        {canManage &&
          (stage === 'post_award_docs' || stage === 'final_io_approval') && (
            <>
              <Divider />
              <Typography variant="subtitle2">Post-award docs & ACH</Typography>
              <Button
                variant="outlined"
                disabled={busy}
                onClick={onUploadClick}
              >
                Upload enrollment document
              </Button>
              <TextField
                label="ACH last4"
                value={achLast4}
                onChange={(e) => onAchLast4Change(e.target.value)}
                inputProps={{ maxLength: 4 }}
              />
              <TextField
                label="ACH plaintext (routing+account)"
                value={achPlaintext}
                onChange={(e) => onAchPlaintextChange(e.target.value)}
                helperText="Sent to peo-lockdown when configured; otherwise paste a pre-created token below."
              />
              <TextField
                label="ACH vault token (optional)"
                value={achToken}
                onChange={(e) => onAchTokenChange(e.target.value)}
              />
              <Button
                disabled={
                  busy || achLast4.length !== 4 || (!achPlaintext && !achToken)
                }
                onClick={onSaveAch}
              >
                Save ACH
              </Button>
              <Divider />
              <Typography variant="subtitle2">Final IO approval</Typography>
              <TextField
                label="Installment count"
                type="number"
                value={installmentCount}
                onChange={(e) => onInstallmentCountChange(e.target.value)}
              />
              <TextField
                label="Payment date 1"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={date1}
                onChange={(e) => onDate1Change(e.target.value)}
              />
              {Number(installmentCount) > 1 && (
                <TextField
                  label="Payment date 2"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={date2}
                  onChange={(e) => onDate2Change(e.target.value)}
                />
              )}
              <TextField
                label="Payee name"
                value={payeeName}
                onChange={(e) => onPayeeNameChange(e.target.value)}
              />
              <Button
                variant="contained"
                disabled={busy || !date1 || !payeeName}
                onClick={onFinalApprove}
              >
                Final approve & schedule payments
              </Button>
              <Typography variant="caption" color="text.secondary">
                Award {application.currency} {amount.toFixed(2)}
              </Typography>
            </>
          )}
      </Stack>
    </Paper>
  );
}
